/* La Sura - the cake builder's running summary and sticky bar (loop 3).
   Reads the page, never writes the form; the bar presses the real add
   button. Total: the variant plus data-add-on-price (minor units). A colour
   picker's code is left out: the choice's name says it.
   See blocks/lasura-cake-summary.liquid. */
import { formatMoney } from '@theme/money-formatting';

class LasuraCakeSummary extends HTMLElement {
  connectedCallback() {
    try {
      this.cfg = JSON.parse(this.querySelector('[data-summary-config]').textContent);
    } catch {
      return;
    }
    this.form = document.getElementById(this.dataset.formId);
    this.scope = this.closest('.shopify-section') || document;
    this.ref = (name) => this.querySelector(`[data-summary-${name}]`);
    this.ref('card').hidden = false;
    this.update = () => requestAnimationFrame(() => this.render());
    ['change', 'input', 'variant:update'].forEach((type) => document.addEventListener(type, this.update));
    this.addEventListener('click', (event) => this.onClick(event));
    this.watchButton();
    this.render(true);
  }

  disconnectedCallback() {
    if (!this.update) return;
    ['change', 'input', 'variant:update'].forEach((type) => document.removeEventListener(type, this.update));
    this.observer?.disconnect();
    document.documentElement.style.removeProperty('scroll-padding-bottom');
  }

  fill(template, values) {
    return Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template || '');
  }

  money(cents) {
    return formatMoney(Number(cents) || 0, this.cfg.moneyFormat, this.cfg.currency);
  }

  /* A made-to-quote choice is picked: the bar starts an enquiry instead. */
  enquiryLink() {
    return this.scope.querySelector('[data-builder-enquiry]:not([hidden]) a');
  }

  realButton() {
    return this.form?.querySelector('button[type="submit"]');
  }

  /* The bar shows only while the page's own add button is out of view, and
     while it shows, scroll padding keeps any focused field above it. */
  watchButton() {
    const button = this.realButton();
    const bar = this.ref('bar');
    if (!button || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting;
      bar.hidden = !show;
      const root = document.documentElement.style;
      /* No height: the bar is hidden by the Step by step layout, whose own
         bar sets the padding (blocks/lasura-cake-steps.liquid). */
      if (show && bar.offsetHeight) root.setProperty('scroll-padding-bottom', `${bar.offsetHeight + 16}px`);
      else root.removeProperty('scroll-padding-bottom');
    });
    this.observer.observe(button);
  }

  onClick(event) {
    const row = event.target.closest('[data-summary-target]');
    if (row) {
      const target = document.getElementById(row.dataset.summaryTarget);
      if (!target) return;
      target.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      const input = target.querySelector('input:checked:not(:disabled)') || [...target.querySelectorAll('input:not([type="hidden"]):not(:disabled)')].find((i) => !i.closest('[hidden]'));
      input?.focus({ preventScroll: true });
      return;
    }
    if (event.target.closest('[data-summary-add]')) (this.enquiryLink() || this.realButton())?.click();
  }

  /* Every row of the summary: size and height from Horizon's picker, then
     each builder group in page order. */
  rows() {
    const rows = [];
    this.scope.querySelectorAll('variant-picker fieldset').forEach((fieldset, i) => {
      const label = fieldset.querySelector('legend')?.childNodes[0]?.textContent.trim();
      const value = fieldset.querySelector('input:checked')?.value;
      if (!fieldset.id) fieldset.id = `LasuraSummaryOption-${i}`;
      if (label) rows.push({ label, value, required: true, id: fieldset.id });
    });
    this.scope.querySelectorAll('lasura-cake-builder [data-builder-group], [data-builder-message-group], lasura-cake-reference').forEach((group, i) => {
      if (!group.id) group.id = `LasuraSummaryGroup-${i}`;
      const label = group.dataset.groupLabel || group.querySelector('legend, label, .lasura-ref__legend')?.textContent.trim();
      let value = '';
      let required = group.hasAttribute('data-required');
      if (group.matches('[data-builder-message-group]')) {
        value = group.querySelector('input')?.value.trim();
      } else if (group.matches('lasura-cake-reference')) {
        const count = [...group.querySelectorAll('input[type="file"]')].filter((f) => f.files?.length).length;
        value = count ? this.fill(this.cfg.text.photos, { count }) : '';
      } else if (group.querySelector('[data-builder-phone]')) {
        const phone = group.querySelector('[data-builder-phone]');
        required = phone.required;
        const method = group.querySelector('input[type="radio"]:checked')?.value;
        value = phone.value.trim() ? [phone.value.trim(), method].filter(Boolean).join(', ') : '';
      } else {
        value = [...group.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked, [data-detail]:not(:disabled):not([type=color])')]
          .filter((input) => input.value.trim() && !input.closest('[hidden]'))
          .map((input) => input.value.trim())
          .join(', ');
        if (group.hasAttribute('data-builder-dietary')) required = true;
      }
      if (label) rows.push({ label, value, required, id: group.id });
    });
    return rows;
  }

  extrasTotal() {
    return [...this.scope.querySelectorAll('[data-add-on-price]')]
      .filter((card) => !card.hidden && !card.closest('[hidden]') && card.querySelector('input:checked'))
      .reduce((sum, card) => sum + Number(card.dataset.addOnPrice || 0), 0);
  }

  render(first = false) {
    const t = this.cfg.text;
    const rows = this.rows();
    /* Rows are rebuilt only when the set of groups changes; otherwise their
       text is updated in place, so a keyboard user's focus is never lost. */
    const list = this.ref('list');
    const signature = rows.map((row) => row.id).join('|');
    if (signature !== this.signature) {
      this.signature = signature;
      list.replaceChildren(
        ...rows.map((row) => {
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'lasura-summary__row';
          button.dataset.summaryTarget = row.id;
          ['label', 'value', 'change'].forEach((part) => {
            const span = document.createElement('span');
            span.className = `lasura-summary__${part}`;
            button.append(span);
          });
          li.append(button);
          return li;
        })
      );
    }
    rows.forEach((row, i) => {
      const button = list.children[i].firstChild;
      const [label, value, change] = button.children;
      label.textContent = row.label;
      value.textContent = row.value || (row.required ? t.missing : t.none);
      value.toggleAttribute('data-missing', !row.value && row.required);
      change.textContent = t.change;
    });

    const required = rows.filter((row) => row.required);
    this.ref('progress').textContent = this.fill(t.progress, { done: required.filter((row) => row.value).length, total: required.length });

    const variantId = new FormData(this.form || undefined).get('id');
    const cake = Number(this.cfg.variants[variantId] ?? 0);
    const extras = this.extrasTotal();
    const total = this.money(cake + extras);
    this.ref('total').textContent = total;
    this.ref('bar-total').textContent = total;
    const breakdown = this.ref('breakdown');
    breakdown.hidden = extras === 0;
    breakdown.textContent = extras ? this.fill(t.breakdown, { cake: this.money(cake), extras: this.money(extras) }) : '';
    if (!first && this.lastTotal && this.lastTotal !== total) this.ref('live').textContent = this.fill(t.announce, { total });
    this.lastTotal = total;

    /* For the preview (assets/lasura-cake-choices.js). */
    const cents = cake + extras;
    const delta = first || this.lastCents == null ? 0 : cents - this.lastCents;
    this.lastCents = cents;
    this.lastDetail = {
      rows: rows.map((row) => ({ ...row, shown: row.value || (row.required ? t.missing : t.none) })),
      total,
      delta,
      deltaText: delta ? (delta > 0 ? '+' : '\u2212') + this.money(Math.abs(delta)) : '',
      first
    };
    this.dispatchEvent(new CustomEvent('lasura:cake-summary', { bubbles: true, detail: this.lastDetail }));

    const source = this.scope.querySelector('lasura-cake-builder [data-builder-allergens]');
    const allergens = this.ref('allergens');
    allergens.hidden = !source || source.hidden;
    allergens.textContent = source && !source.hidden ? source.textContent : '';

    const preview = this.scope.querySelector('lasura-cake-preview svg');
    const thumb = this.ref('thumb');
    if (preview) {
      /* A copy, so its gradient and clip ids are renamed: duplicate ids on
         one page break the references and trip accessibility checks. */
      const copy = preview.cloneNode(false);
      copy.removeAttribute('role');
      copy.removeAttribute('aria-label');
      copy.innerHTML = preview.innerHTML.replace(/id="([^"]+)"/g, 'id="t$1"').replace(/url\(#([^)]+)\)/g, 'url(#t$1)');
      thumb.replaceChildren(copy);
    }
    thumb.hidden = !preview;

    const real = this.realButton();
    const add = this.ref('add');
    const enquiry = !!this.enquiryLink();
    this.toggleAttribute('data-enquiry', enquiry);
    if (real) add.disabled = real.disabled && !enquiry;
  }
}

if (!customElements.get('lasura-cake-summary')) customElements.define('lasura-cake-summary', LasuraCakeSummary);
